import type { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';
import { useGetEntityByRefCallback } from './useGetEntityByRefCallback';
import { normalizeEntityRef } from '@internal/backstage-plugin-api-catalog-common';

/**
 * Fetch a entity by its reference.
 *
 * @param entityRef The reference of the entity to fetch. Can be a string or a CompoundEntityRef.
 * @param options The to do additional filtering
 */
export const useGetEntityByRef = <T extends Entity>(
  entityRef: CompoundEntityRef | string | null | undefined,
  options?: {
    fallbackKind?: string;
    predicate?: (entity: Entity) => entity is T;
  },
) => {
  const callback = useGetEntityByRefCallback();
  const { fallbackKind, predicate } = options || {};
  let resolvedEntityRef: string | null = null;
  if (!entityRef) {
    resolvedEntityRef = null;
  } else if (typeof entityRef === 'string') {
    resolvedEntityRef = normalizeEntityRef(entityRef, fallbackKind);
  } else {
    resolvedEntityRef = stringifyEntityRef(entityRef);
  }
  return useAsync(async () => {
    if (!resolvedEntityRef) {
      return undefined;
    }
    const result = await callback(resolvedEntityRef, {});
    if (predicate && result) {
      return predicate(result) ? result : undefined;
    }
    return result as T;
  }, [callback, resolvedEntityRef, predicate]);
};
